import { Controller } from "@hotwired/stimulus"

// Debounced autosave for the in-app FormSubmission edit form.
//
// Wiring (set on the <form> element):
//   data-controller="form-submission-autosave"
//   data-form-submission-autosave-url-value="<turbo_stream PATCH url>"
//
// Status target (anywhere inside the form):
//   data-form-submission-autosave-target="status"
//
// Behavior:
//   - Listens for input/change events on form fields, debounces ~1500ms.
//   - PATCHes the form body via fetch (Accept: text/vnd.turbo-stream.html).
//     The controller responds 204 No Content; we just flip the status pill.
//   - In-flight requests are aborted when a newer save fires (AbortController).
//   - "Save draft" (manual submit) bypasses autosave and uses the form's
//     standard Turbo submission — no special handling needed.
//
// Mobile: the status pill is small, non-blocking, and aria-live="polite"
// so screen readers announce save state without stealing focus.
export default class extends Controller {
  static targets = ["status"]
  static values  = { url: String, debounce: { type: Number, default: 1500 } }

  connect() {
    this.timer = null
    this.controller = null
    this.savedTimer = null
    this.boundOnInput = this.onInput.bind(this)
    this.element.addEventListener("input", this.boundOnInput)
    this.element.addEventListener("change", this.boundOnInput)
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundOnInput)
    this.element.removeEventListener("change", this.boundOnInput)
    if (this.timer) clearTimeout(this.timer)
    if (this.savedTimer) clearTimeout(this.savedTimer)
    if (this.controller) this.controller.abort()
  }

  onInput() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.debounceValue)
  }

  async save() {
    // Cancel any in-flight save: a newer one supersedes it.
    if (this.controller) this.controller.abort()
    this.controller = new AbortController()

    this.setStatus("Saving…", false)

    const url    = this.urlValue || this.element.action
    const token  = document.querySelector("meta[name='csrf-token']")?.content
    const body   = new FormData(this.element)

    try {
      const response = await fetch(url, {
        method:  "PATCH",
        headers: {
          "Accept":       "text/vnd.turbo-stream.html",
          "X-CSRF-Token": token || ""
        },
        body,
        signal:      this.controller.signal,
        credentials: "same-origin"
      })

      if (response.ok) {
        this.setStatus("Saved", true)
      } else {
        this.setStatus("Couldn't save — try again", false)
      }
    } catch (err) {
      if (err.name === "AbortError") return  // superseded; stay quiet
      this.setStatus("Couldn't save — try again", false)
    }
  }

  setStatus(text, fade) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.style.opacity = "1"

    if (this.savedTimer) clearTimeout(this.savedTimer)
    if (fade) {
      this.savedTimer = setTimeout(() => {
        this.statusTarget.style.transition = "opacity 0.4s"
        this.statusTarget.style.opacity = "0"
      }, 2000)
    }
  }
}
